import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import SQLEditor from '../components/SQLEditor';
import ResultsTable from '../components/ResultsTable';
import ChartPanel from '../components/ChartPanel';
import UploadZone from '../components/UploadZone';
import { useDuckDB } from '../useDuckDB';
import { Table2, Code2, BarChart2, Upload, Menu, X } from 'lucide-react';

const SAMPLE_QUERY = `-- Upload a CSV, then query it here.
-- Example:
-- SELECT * FROM my_table LIMIT 100;`;

function useDrag(onDrag) {
  const dragging = useRef(false);
  const start = useRef(0);
  const onMouseDown = useCallback((e) => {
    dragging.current = true;
    start.current = e.clientY;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const move = (ev) => {
      if (!dragging.current) return;
      onDrag(ev.clientY - start.current);
      start.current = ev.clientY;
    };
    const up = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [onDrag]);
  return onMouseDown;
}

// Use matchMedia so it fires correctly on iOS Safari immediately on load
function useIsMobile() {
  const mq = useRef(window.matchMedia('(max-width: 767px)'));
  const [isMobile, setIsMobile] = useState(mq.current.matches);
  useEffect(() => {
    const handler = (e) => setIsMobile(e.matches);
    mq.current.addEventListener('change', handler);
    return () => mq.current.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function CSVQueryTool() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { tables, loading, registerCSV, runQuery, ensureDB } = useDuckDB();

  const [sql, setSql] = useState(SAMPLE_QUERY);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const [running, setRunning] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [editorHeight, setEditorHeight] = useState(200);
  const [mobileTab, setMobileTab] = useState('editor');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  useEffect(() => { ensureDB(); }, [ensureDB]);

  const handleRun = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setQueryError(null);
    try {
      const result = await runQuery(sql);
      setQueryResult(result);
      setShowChart(false);
      if (isMobile) setMobileTab('results');
    } catch (err) {
      setQueryError(err.message || String(err));
      setQueryResult(null);
      if (isMobile) setMobileTab('results');
    } finally {
      setRunning(false);
    }
  }, [sql, runQuery, running, isMobile]);

  const handleFiles = useCallback(async (files) => {
    for (const file of files) {
      const tableName = await registerCSV(file);
      setSql(`SELECT * FROM "${tableName}" LIMIT 100;`);
    }
    setShowUpload(false);
    setShowMobileSidebar(false);
    if (isMobile) setMobileTab('editor');
  }, [registerCSV, isMobile]);

  const handleTableClick = useCallback((tableName) => {
    setSql(`SELECT * FROM "${tableName}" LIMIT 100;`);
    setShowMobileSidebar(false);
    if (isMobile) setMobileTab('editor');
  }, [isMobile]);

  const onDrag = useCallback((delta) => {
    setEditorHeight(h => Math.max(100, Math.min(600, h + delta)));
  }, []);
  const onMouseDown = useDrag(onDrag);

  const hasResults = queryResult?.rows;
  const resultCount = hasResults ? queryResult.rows.length : 0;

  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',        /* dynamic viewport — accounts for iOS chrome */
        background: 'var(--bg)',
        overflow: 'hidden',
        position: 'fixed',       /* prevents rubber-band scroll revealing blank */
        inset: 0,
      }}>
        {/* ── Top bar ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 12px', height: 48, flexShrink: 0,
          background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
          WebkitBackfaceVisibility: 'hidden', /* iOS compositing hint */
        }}>
          <button
            onClick={() => setShowMobileSidebar(true)}
            style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', padding: 6, display: 'flex', WebkitTapHighlightColor: 'transparent' }}
          >
            <Menu size={18} />
          </button>
          <span style={{ fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 14, flex: 1 }}>
            maca<span style={{ color: 'var(--orange)' }}>row</span>ni
            <span style={{ color: 'var(--border2)', margin: '0 5px' }}>/</span>
            <span style={{ color: 'var(--text2)', fontWeight: 400 }}>csv_query</span>
          </span>
          <button
            onClick={() => setShowUpload(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '7px 13px', background: 'var(--accent)',
              border: 'none', borderRadius: 4,
              color: '#fff', fontFamily: 'var(--sans)', fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Upload size={12} />
            Import
          </button>
        </div>

        {/* ── Tab content — fills remaining space between topbar and tabbar ── */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: mobileTab === 'editor'  ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
            <SQLEditor value={sql} onChange={setSql} onRun={handleRun} running={running} tables={tables} />
          </div>
          <div style={{ display: mobileTab === 'results' ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
            <ResultsTable
              rows={queryResult?.rows}
              columns={queryResult?.columns}
              error={queryError}
              onVisualizeClick={() => setMobileTab('chart')}
            />
          </div>
          <div style={{ display: mobileTab === 'chart' && hasResults ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
            {hasResults && (
              <ChartPanel rows={queryResult.rows} columns={queryResult.columns} onClose={() => setMobileTab('results')} />
            )}
          </div>
        </div>

        {/* ── Bottom tab bar ── */}
        <div style={{
          display: 'flex',
          flexShrink: 0,
          height: 56,
          background: 'var(--bg2)',
          borderTop: '1px solid var(--border)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          /* ensure it's always on top and visible */
          position: 'relative',
          zIndex: 10,
        }}>
          {[
            { id: 'editor',  label: 'SQL',     Icon: Code2    },
            { id: 'results', label: resultCount > 0 ? `${resultCount.toLocaleString()} rows` : 'Results', Icon: Table2, },
            { id: 'chart',   label: 'Chart',   Icon: BarChart2, disabled: !hasResults },
          ].map(({ id, label, Icon, disabled }) => (
            <button
              key={id}
              onClick={() => !disabled && setMobileTab(id)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                borderTop: `2px solid ${mobileTab === id ? 'var(--accent)' : 'transparent'}`,
                color: disabled ? 'var(--text3)' : mobileTab === id ? 'var(--accent)' : 'var(--text2)',
                cursor: disabled ? 'default' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
                transition: 'color 0.1s',
                padding: '8px 0 4px',
              }}
            >
              <Icon size={18} />
              <span style={{ fontSize: 10, fontFamily: 'var(--sans)', fontWeight: 500 }}>{label}</span>
            </button>
          ))}
        </div>

        {/* ── Sidebar drawer ── */}
        {showMobileSidebar && (
          <>
            <div
              onClick={() => setShowMobileSidebar(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 50 }}
            />
            <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 280, zIndex: 51, display: 'flex', flexDirection: 'column' }}>
              <Sidebar
                tables={tables}
                onUploadClick={() => { setShowMobileSidebar(false); setShowUpload(true); }}
                onTableClick={handleTableClick}
                onHome={() => navigate('/')}
              />
              <button
                onClick={() => setShowMobileSidebar(false)}
                style={{
                  position: 'absolute', top: 10, right: -44,
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: '0 4px 4px 0', color: 'var(--text2)',
                  padding: '7px 9px', cursor: 'pointer', display: 'flex',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <X size={16} />
              </button>
            </div>
          </>
        )}

        {showUpload && (
          <UploadZone onFiles={handleFiles} loading={loading} onClose={() => setShowUpload(false)} />
        )}
      </div>
    );
  }

  // ── DESKTOP ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', background: 'var(--bg)' }}>
      <Sidebar tables={tables} onUploadClick={() => setShowUpload(true)} onTableClick={handleTableClick} onHome={() => navigate('/')} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <div style={{ height: editorHeight, flexShrink: 0, overflow: 'hidden' }}>
          <SQLEditor value={sql} onChange={setSql} onRun={handleRun} running={running} tables={tables} />
        </div>
        <div
          onMouseDown={onMouseDown}
          style={{ height: 5, background: 'var(--border)', cursor: 'row-resize', flexShrink: 0, transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--border)'}
        />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: showChart && hasResults ? '0 0 40%' : 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderBottom: showChart && hasResults ? '1px solid var(--border)' : 'none' }}>
            <ResultsTable rows={queryResult?.rows} columns={queryResult?.columns} error={queryError} onVisualizeClick={() => setShowChart(true)} />
          </div>
          {showChart && hasResults && (
            <div style={{ flex: '0 0 60%', overflow: 'hidden' }}>
              <ChartPanel rows={queryResult.rows} columns={queryResult.columns} onClose={() => setShowChart(false)} />
            </div>
          )}
        </div>
      </div>
      {showUpload && <UploadZone onFiles={handleFiles} loading={loading} onClose={() => setShowUpload(false)} />}
      <DragDropOverlay onFiles={handleFiles} />
    </div>
  );
}

function DragDropOverlay({ onFiles }) {
  const [over, setOver] = useState(false);
  const counter = useRef(0);
  useEffect(() => {
    const enter = (e) => { if ([...e.dataTransfer.types].includes('Files')) { counter.current++; setOver(true); } };
    const leave = () => { counter.current--; if (counter.current <= 0) { counter.current = 0; setOver(false); } };
    const drop = (e) => { counter.current = 0; setOver(false); e.preventDefault(); const files = [...e.dataTransfer.files].filter(f => f.name.endsWith('.csv')); if (files.length) onFiles(files); };
    const over_ = (e) => e.preventDefault();
    window.addEventListener('dragenter', enter); window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop); window.addEventListener('dragover', over_);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragleave', leave); window.removeEventListener('drop', drop); window.removeEventListener('dragover', over_); };
  }, [onFiles]);
  if (!over) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(91,184,245,0.08)', border: '2px dashed var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 20, fontWeight: 600, color: 'var(--accent)' }}>Drop CSV files anywhere</div>
    </div>
  );
}
